import { Dropdown, Button, Message } from "../BP2Components";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { setUrlTo } from "../FormsV2/GeneralScripts";
import ActionsModal from "./ActionsModal";
import "./ActionsDropdown.less";
import IntlMessages from "../../util/IntlMessages";
import CreateChat from "../Widgets/WidgetsSettings/ChatSettings/CreateChat";
import { addRowValuesToConnection } from "../../util/Scripts";
import { setBackUrl } from "../../appRedux/actions/Auth";
import { runListAction } from "../../services/api/Api";
import { base64ToBlob, getMimeType } from "../FormsV2/FormAttachments/FormAttachments";
import { downloadAttachment } from "../FormsV2/MessageButtons/Modals/MailModal";
import { useIntl } from "react-intl";
import { googleNav } from "../../util/actions/googleNav";
import { DROPDOWN_ITEM_SELECTOR } from "../../util/ListGridNavWrapper";
import { FormV3InModal } from "../Widgets/FormsV3/FormV3InModal/FormV3InModal";
import { handlePocResponse } from "../../util/pocResponseHandler";
import { downloadXml, ensureXmlHeader } from "../../util/actions/downloadXML";
import { persistor } from "../../appRedux/store/Store";

export const findPopup = () => {
   const root = document.getElementById("dropdown-root");
   return (
      root?.querySelector(".items-container") ||
      root?.querySelector('[role="menu"]') ||
      root?.querySelector("[data-bp2-dropdown]") ||
      null
   );
};

export const focusNext = (popup, dir) => {
   const items = Array.from(popup.querySelectorAll(DROPDOWN_ITEM_SELECTOR)).filter(
      (el) => !el.disabled && el.tabIndex !== -1,
   );
   if (!items.length) return;
   let i = items.indexOf(document.activeElement);
   if (i < 0) i = 0;
   const next = dir > 0 ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
   items[next]?.focus?.({ preventScroll: true });
};

const ActionsDropdown = ({
   urlButton,
   actions,
   hiddenDropdown,
   rowId,
   rowData,
   insideListWidget = false,
   centeredButtons = false,
   widgetContext,
   btnClassName = "",
   children,
   saveCallback = null,
   listId,
   formAction,
}) => {
   const [modalData, setModalData] = useState({ actionId: null, recordId: null, isOpen: false });
   const [isDropdownOpen, setIsDropdownOpen] = useState(false);
   const [isOpenModalDataPoc, setIsOpenModalDataPoc] = useState(false);
   const [pocData, setPocData] = useState({});

   const dispatch = useDispatch();
   const intl = useIntl();
   const listActions = rowData?.api_recordActions || rowData?.api_recordactions;

   const closeDropdownAndNotify = () => {
      setIsDropdownOpen(false);
      suppressNextToggleRef.current = Date.now() + 250;
      try {
         document.dispatchEvent(new CustomEvent("dropdown/close", { detail: { rowId } }));
      } catch (e) {
         console.error(e);
      }
   };

   const handleClick = async ({ key, formId, item }) => {
      closeDropdownAndNotify();
      const { actionType, connectionsLink, url, label } = item.props;
      if (connectionsLink && rowData) {
         const targetUrl = addRowValuesToConnection(connectionsLink, rowData, insideListWidget);
         setUrlTo(targetUrl);
         return;
      }
      const isPoC = String(listId ?? "").length > 10;
      if (!isPoC) {
         const actionKey = listActions.find((act) => act.id === key)?.key;
         const actionKeyBase64 = actionKey ? btoa(actionKey) : null;
         if (
            url &&
            (url.includes("/safeDownloadFileFromSFA") || url.includes("/safeDownloadFile"))
         ) {
            Message.loading(intl.formatMessage({ id: "actions.fileDownload" }));
            try {
               const res = await runListAction(key, rowId, actionKey);
               const { base64File, filename } = res.data;
               if (base64File && filename) {
                  const blob = base64ToBlob(base64File, getMimeType(filename));
                  downloadAttachment({ name: filename }, blob);
                  Message.success(intl.formatMessage({ id: "actions.fileDownloadSuccess" }));
               }
            } catch (error) {
               Message.error(intl.formatMessage({ id: "actions.fileDownloadError" }));
               console.error(error);
            } finally {
               Message.destroy();
            }
         }
         if (!actionType) return;
         switch (actionType) {
            case "GOOGLE_NAV":
               await googleNav({
                  actionId: key,
                  recordId: rowId,
                  intl,
                  actionKey,
               });
               break;
            case "FORM":
               setModalData({
                  actionId: key,
                  recordId: rowId,
                  isOpen: true,
                  formKey: actionKey,
                  formId: formId,
                  label,
               });
               break;
            case "PROCESS":
               const searchParams = new URLSearchParams({
                  actionId: key,
                  recordId: rowId,
                  actionKey: actionKeyBase64,
               });
               const targetPath = `/cases/${key}?${searchParams.toString()}`;
               dispatch(setBackUrl(window.location.pathname));
               await persistor.flush();
               setUrlTo(targetPath);
               break;
            case "OPEN_URL": {
               const targetUrl = rowData?.[item.props.urlColumn];
               let parsedUrl;
               try {
                  parsedUrl = targetUrl ? new URL(targetUrl) : null;
               } catch {
                  parsedUrl = null;
               }
               if (
                  parsedUrl &&
                  (parsedUrl.protocol === "https:" || parsedUrl.protocol === "http:")
               ) {
                  window.open(targetUrl, "_blank", "noopener,noreferrer");
               } else {
                  Message.error(intl.formatMessage({ id: "actions.openUrlError" }));
               }
               break;
            }

            case "DOWNLOAD_XML": {
               Message.loading(intl.formatMessage({ id: "actions.fileDownload" }));
               try {
                  const xmlActionSafetyKey = actionsSafety ? actionKey : null;
                  const xmlRes = await runListAction(key, rowId, xmlActionSafetyKey);
                  const xml = xmlRes.data?.actions?.xml;
                  const fileName = xmlRes.data?.actions?.file_name;
                  if (xml) {
                     downloadXml(ensureXmlHeader(xml), fileName);
                     Message.success(intl.formatMessage({ id: "actions.fileDownloadSuccess" }));
                  } else {
                     Message.error(intl.formatMessage({ id: "actions.fileDownloadError" }));
                  }
               } catch (error) {
                  Message.error(intl.formatMessage({ id: "actions.fileDownloadError" }));
                  console.error(error);
               } finally {
                  Message.destroy();
               }
               break;
            }
         }
      } else {
         await handlePocResponse(item.props.url, {
            rowId,
            callbacks: {
               onOpenForm: (data) => {
                  setIsOpenModalDataPoc(true);
                  setPocData(data);
               },
               onOpenUrl: (url) => setUrlTo(url),
               onRefreshList: () => window.location.reload(),
               onOpenWorkflow: (instanceId, rowId) => {
                  // may not work, can't find this case in POC table widget
                  const searchParams = new URLSearchParams({
                     recordId: rowId,
                     instanceId,
                  });
                  const targetPath = `/cases/${rowId}?${searchParams.toString()}`;
                  setUrlTo(targetPath);
                  dispatch(setBackUrl(window.location.pathname));
               },
               onPopup: ({ message, buttons }) => {
                  Message.info(message, 0, buttons);
               },
            },
         });
      }
   };

   const menu = useMemo(() => {
      if (hiddenDropdown) {
         // Ensure actions is an array before calling map
         const actionsArray = Array.isArray(actions) ? actions : [];

         return (
            <ul className="actions-dropdown__hidden-dropdown" data-items-center={centeredButtons}>
               {actionsArray.map((action, index) => (
                  <li key={action.id ?? `${action.name}-${index}`}>
                     <Button
                        buttonType="primary"
                        onClick={() =>
                           handleClick({
                              key: action.id ?? `${action.name}-${index}`,
                              formId: action?.formID,
                              item: {
                                 props: {
                                    actionType: action?.type,
                                    connectionsLink: action?.connectionsLink,
                                    url: action._url ?? action.url,
                                    urlColumn: widgetContext?.columns?.find(
                                       (col) =>
                                          col.techName === action?.urlColumn ||
                                          col.label === action?.urlColumn,
                                    )?.dataIndex,
                                 },
                              },
                           })
                        }
                     >
                        {action?.label ?? action?.name}
                     </Button>
                  </li>
               ))}
               {widgetContext?.showPrivateChatButtons && (
                  <CreateChat
                     chatTitleKey={widgetContext.chatPrivateButtonTitle}
                     isChatButtonVisible={widgetContext?.showPrivateChatButtons}
                     currentWidget={widgetContext.currentWidget}
                     columns={widgetContext.columns}
                     rowData={rowData}
                     chatType="privateChat"
                     onChatCreate={() => setIsDropdownOpen(false)}
                  />
               )}
               {widgetContext?.showPublicChatButtons && (
                  <CreateChat
                     chatTitleKey={widgetContext.chatPublicButtonTitle}
                     isChatButtonVisible={widgetContext?.showPublicChatButtons}
                     currentWidget={widgetContext.currentWidget}
                     columns={widgetContext.columns}
                     rowData={rowData}
                     chatType="publicChat"
                     onChatCreate={() => setIsDropdownOpen(false)}
                  />
               )}
            </ul>
         );
      }

      // Ensure actions is an array before calling map
      const actionsArray = Array.isArray(actions) ? actions : [];

      let dropdownList = actionsArray
         ?.map((action, index) => {
            const itemActions = rowData?.api_recordactions ?? rowData?.api_recordActions ?? [];
            if (itemActions.length > 0 && !JSON.stringify(itemActions).includes(action?.id)) {
               return null;
            }
            return {
               id: action.id ?? `${action.name}-${index}`,
               name: action?.label ?? action?.name,
               onClick: () =>
                  handleClick({
                     key: action.id ?? `${action.name}-${index}`,
                     formId: action?.formID,
                     item: {
                        props: {
                           actionType: action?.type,
                           connectionsLink: action?.connectionsLink,
                           url: action?._url ?? action.url,
                           label: action.label ?? action.name,
                           urlColumn: widgetContext?.columns?.find(
                              (col) =>
                                 col.techName === action?.urlColumn ||
                                 col.label === action?.urlColumn,
                           )?.dataIndex,
                        },
                     },
                  }),
            };
         })
         .filter((item) => item !== null);

      if (widgetContext?.showPrivateChatButtons) {
         dropdownList.push({
            id: "privateChat",
            name: (
               <CreateChat
                  chatTitleKey={widgetContext.chatPrivateButtonTitle}
                  isChatButtonVisible={widgetContext?.showPrivateChatButtons}
                  currentWidget={widgetContext.currentWidget}
                  columns={widgetContext.columns}
                  rowData={rowData}
                  chatType="privateChat"
                  onChatCreate={() => setIsDropdownOpen(false)}
               />
            ),
            customButton: true,
         });
      }

      if (urlButton?.showUrlButton) {
         dropdownList.push({
            id: "NavigationButton",
            name: (
               <a
                  className="dropdown-button-item"
                  href={rowData?.[urlButton?.urlColumn?.label]}
                  target="_blank"
                  rel="noreferrer"
               >
                  <IntlMessages id={urlButton?.ulrButtonTitle} />
               </a>
            ),
            customButton: true,
         });
      }

      if (widgetContext?.showPublicChatButtons) {
         dropdownList.push({
            id: "publicChat",
            name: (
               <CreateChat
                  chatTitleKey={widgetContext.chatPublicButtonTitle}
                  isChatButtonVisible={widgetContext?.showPublicChatButtons}
                  currentWidget={widgetContext.currentWidget}
                  columns={widgetContext.columns}
                  rowData={rowData}
                  chatType="publicChat"
                  onChatCreate={() => setIsDropdownOpen(false)}
               />
            ),
            customButton: true,
         });
      }
      return dropdownList;
   }, [
      hiddenDropdown,
      centeredButtons,
      widgetContext?.chatTitleKey,
      widgetContext?.currentWidget,
      widgetContext?.columns,
      rowData,
      actions,
      listActions,
   ]);

   const suppressNextToggleRef = useRef(0);
   const ignoreAfterModalCloseRef = useRef(0);

   useEffect(() => {
      const onForceClose = () => {
         setIsDropdownOpen(false);
         suppressNextToggleRef.current = Date.now() + 250;
      };
      document.addEventListener("dropdown/close", onForceClose);
      return () => {
         document.removeEventListener("dropdown/close", onForceClose);
      };
   }, [rowId]);

   useEffect(() => {
      if (!isDropdownOpen) return;

      let cancelled = false;
      let tries = 0;

      const focusWhenPopupIsReady = () => {
         if (cancelled) return;
         const popup = findPopup();
         if (popup) {
            if (!popup.hasAttribute("tabindex")) popup.setAttribute("tabindex", "-1");

            // find first focusable element
            const items = Array.from(popup.querySelectorAll(DROPDOWN_ITEM_SELECTOR)).filter(
               (el) => !el.disabled && el.tabIndex !== -1,
            );
            (items[0] ?? popup).focus({ preventScroll: true });
            return;
         }
         // wait for render dropdown
         if (++tries < 80) requestAnimationFrame(focusWhenPopupIsReady);
      };

      requestAnimationFrame(focusWhenPopupIsReady);
      return () => {
         cancelled = true;
      };
   }, [isDropdownOpen]);

   useEffect(() => {
      if (!isDropdownOpen) return;

      // find dropdown container
      const getPopup = () => {
         const root = document.getElementById("dropdown-root");
         if (!root) return null;
         return (
            root.querySelector(".items-container") ||
            root.querySelector('[role="menu"]') ||
            root.querySelector("[data-bp2-dropdown]") ||
            null
         );
      };

      const popup = getPopup();
      if (!popup) return;

      //  close when focus i out of dropdown(Tab / Tab+Shift)
      const onFocusOut = (e) => {
         // new focus
         const next = e.relatedTarget || document.activeElement || null;

         // if next focus isn't in dropdown - close dropdown
         const leavingPopup = !next || !popup.contains(next);
         if (leavingPopup) {
            setIsDropdownOpen(false);
         }
      };

      popup.addEventListener("focusout", onFocusOut, true);

      return () => {
         popup.removeEventListener("focusout", onFocusOut, true);
      };
   }, [isDropdownOpen]);

   useEffect(() => {
      if (!isDropdownOpen) return;

      const onKeyCapture = (e) => {
         const popup = findPopup();
         if (!popup) return;

         const inPopup =
            popup.contains(e.target) || popup.contains(document.activeElement || document.body);
         if (!inPopup) return;

         const stopAll = () => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();
         };

         if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            try {
               setIsDropdownOpen(false);
            } catch (e) {
               console.error(e);
            }

            requestAnimationFrame(() => {
               requestAnimationFrame(() => {
                  // if focus is in cell - blur
                  try {
                     const active = document.activeElement;
                     if (active && popup.contains(active) && typeof active.blur === "function") {
                        active.blur();
                     }
                  } catch {}

                  // find active table and focus cell
                  try {
                     const ownerRoot =
                        document.querySelector(
                           '[data-table-widget="true"][data-bp2-grid-active="true"]',
                        ) ||
                        (document.querySelector(".bp2-active-cell")
                           ? document
                                .querySelector(".bp2-active-cell")
                                .closest?.('[data-table-widget="true"]')
                           : null);

                     const target =
                        ownerRoot?.querySelector('[data-grid-cell="true"][tabindex="0"]') ||
                        ownerRoot?.querySelector('[data-grid-cell="true"]') ||
                        ownerRoot;

                     target?.focus?.({ preventScroll: true });
                  } catch {}
               });
            });

            return;
         }

         if (e.key === "ArrowDown") {
            stopAll();
            focusNext(popup, +1);
            return;
         }
         if (e.key === "ArrowUp") {
            stopAll();
            focusNext(popup, -1);
            return;
         }
         if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            stopAll();
            return;
         }
      };

      window.addEventListener("keydown", onKeyCapture, true);
      return () => window.removeEventListener("keydown", onKeyCapture, true);
   }, [isDropdownOpen]);

   return (
      <>
         {hiddenDropdown ? (
            menu
         ) : (
            <Button
               buttonType="primary"
               style={{ minWidth: 80 }}
               onClick={(e) => {
                  if (
                     Date.now() < suppressNextToggleRef.current ||
                     Date.now() < ignoreAfterModalCloseRef.current
                  ) {
                     e.preventDefault();
                     e.stopPropagation();
                     suppressNextToggleRef.current = 0;
                     return;
                  }
                  setIsDropdownOpen((v) => !v);
               }}
               className={`${btnClassName} bp2-actions-dropdown-btn`}
               data-actions-trigger
               aria-haspopup="menu"
               aria-expanded={isDropdownOpen ? "true" : "false"}
            >
               {children ? children : <IntlMessages id="global.actions" />}
               <Dropdown
                  items={menu}
                  isOpen={isDropdownOpen}
                  setIsOpen={setIsDropdownOpen}
                  customZIndex={1001}
                  noDataInfo={intl.formatMessage({ id: "actions.noDataInfo" })}
               />
            </Button>
         )}
         {!!Object.keys(pocData).length ? (
            <FormV3InModal
               formId={pocData?.id}
               isOpen={isOpenModalDataPoc}
               setIsOpen={() => setIsOpenModalDataPoc(false)}
               pocData={pocData}
               setPocData={setPocData}
            />
         ) : null}
         <ActionsModal
            modalData={modalData}
            handleClose={() => {
               setModalData({ actionId: null, recordId: null, isOpen: false });
               ignoreAfterModalCloseRef.current = Date.now() + 350;
               document.dispatchEvent(new CustomEvent("dropdown/close", { detail: { rowId } }));
            }}
            saveCallback={saveCallback}
         />
      </>
   );
};

ActionsDropdown.propTypes = {
   actions: PropTypes.arrayOf(PropTypes.object),
   rowId: PropTypes.string,
   hiddenDropdown: PropTypes.bool,
   rowData: PropTypes.object,
   // This is needed only for list widget, because of lowercase column names
   insideListWidget: PropTypes.bool,
   centeredButtons: PropTypes.bool,
   widgetContext: PropTypes.objectOf({
      isChatButtonVisible: PropTypes.bool,
      chatTitleKey: PropTypes.string,
      currentWidget: PropTypes.object,
      columns: PropTypes.array,
   }),
   btnClassName: PropTypes.string,
   children: PropTypes.node,
   saveCallback: PropTypes.func,
   listId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ActionsDropdown;
